import React, { useCallback, useRef, useState } from "react";

import { CtaButton } from "../cta-button";
import { Icon, IconName } from "../icon";
import { useDefinition } from "../../hooks/useDefinition";
import { useCart } from "../../hooks/useCart";
import { useBusyBlockerContext } from "../../contexts/busy-blocker-context";
import { useAppContext } from "../../hooks/useAppContext";
import { waitForPendingUploadsAsync } from "../dialogs/pending-uploads-dialog";
import { useMountedState, usePromise } from "react-use";
import { showProductValidationDialogAsync } from "../product-validation-dialog";
import { useNavigateToUrl } from "../../hooks/useNavigate";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { usePreviewContext } from "../../contexts/preview-context";
import { useIsPreviewMode } from "../../hooks/useIsPreviewMode";
import { labels } from "./labels";
import { useLabels } from "../../hooks/useLabels";

interface AddToCartButtonProps {
	showLargeButton: boolean;
	ctaTextButtonRef?: React.RefObject<HTMLButtonElement | null>;
	className?: string;
}

function AddToCartButton({ showLargeButton, ctaTextButtonRef, className }: AddToCartButtonProps) {
	const definition = useDefinition();
	const shoppingCart = useCart();
	const { busyBlocker } = useBusyBlockerContext();
	const [isAddToBasketBusy, setIsAddToBasketBusy] = useState(false);
	const shouldValidate = useRef(true);
	const navigateToUrl = useNavigateToUrl();
	const { goTo } = usePreviewContext();
	const [isPreviewMode, setIsPreviewMode] = useIsPreviewMode();

	const isAddToBasketDisabled = isAddToBasketBusy || !shoppingCart;
	const isPreviewRequired = definition?.product?.isPreviewRequiredMC;

	const ctaButtonIconName: IconName =
		isPreviewRequired && !isPreviewMode ? "angle-right" : "shopping-cart-plus";

	const {
		context,
		editor,
		getFontSizeCorrectorAsync,
		googleAnalytics,
		platform: { queryString },
	} = useAppContext();
	const isUpdateFlow = eXpress.GuidString.isNonEmpty(context.cartItemId);

	let ctaButtonLabel = labels.addToCart;
	if (isUpdateFlow) ctaButtonLabel = labels.updateCart;
	if (isPreviewRequired && !isPreviewMode) ctaButtonLabel = labels.next;

	const [[ctaButtonText], areLabelsLoading] = useLabels([ctaButtonLabel]);

	const resolveIfMounted = usePromise();
	const isMounted = useMountedState();
	const { createErrorHandler } = useErrorHandling();

	const validateDefinitionAsync = useCallback(async () => {
		const messages = await resolveIfMounted(
			// Don't usePromise inside a BalancedScope (like busyBlocker)!
			busyBlocker.while(
				editor.validateAsync({
					getFontSizeCorrectorAsync,
					getLocalizedMonthNames: undefined,
				}),
			),
		);

		const fixableByUser = messages.some((msg) => {
			if (msg.name === "TextOutsideBounds") {
				return msg.details.affectedItems.some(({ canBeFixedByUser }) => canBeFixedByUser);
			}
			return true;
		});
		return messages.length && fixableByUser
			? showProductValidationDialogAsync(messages, isPreviewRequired ? "preview" : "cart")
			: true;
	}, [busyBlocker, editor, getFontSizeCorrectorAsync, isPreviewRequired, resolveIfMounted]);

	const onAddToBasketClick = useCallback(async () => {
		// Log when clicked
		googleAnalytics.trackEventToGa({
			trackingAction: isPreviewMode ? "AddToCartFromPreview" : "AddToCart",
		});

		if (!definition || !shoppingCart) return;

		// Update Cart and navigate away
		const updateCartAsync = () =>
			busyBlocker.while(
				editor.updateShoppingCartAsync().then((result) => {
					const firstAffectedCartItemId = result.cartItemIds?.first();
					if (eXpress.GuidString.isNonEmpty(firstAffectedCartItemId)) {
						window.history.replaceState(
							null,
							"",
							queryString.addOrReplaceQueryStringParameterInUrl(
								window.location.href,
								"cartId",
								firstAffectedCartItemId,
							),
						);
					}

					return result;
				}),
			);

		setIsAddToBasketBusy(true); // prevent button can be clicked again when busy

		try {
			const hasFinishedUploading = await waitForPendingUploadsAsync({
				getHasPendingUploads: () => editor.hasPendingUploads(true),
			});

			if (!hasFinishedUploading) return;

			// validation may have already happened in the preview (for mandatory preview products)
			if (shouldValidate.current) {
				const canContinue = await validateDefinitionAsync();
				shouldValidate.current = !canContinue;
				if (!canContinue) return;
			}

			const { redirectUrl } = await updateCartAsync();

			// on iPhone when navigating away and pressing back,
			// the page will be shown like it was left (no refresh)
			// Therefor we enable the busy button before navigating away
			if (isMounted()) setIsAddToBasketBusy(false);

			navigateToUrl(redirectUrl, {
				waitForPendingUploads: "onlyUsed",
			});
		} catch (error) {
			createErrorHandler()(error);
		} finally {
			if (isMounted()) setIsAddToBasketBusy(false);
		}
	}, [
		googleAnalytics,
		isPreviewMode,
		createErrorHandler,
		editor,
		shoppingCart,
		busyBlocker,
		navigateToUrl,
		isMounted,
		queryString,
		definition,
		validateDefinitionAsync,
	]);

	const onNextClick = useCallback(async () => {
		const canContinue = await validateDefinitionAsync();
		shouldValidate.current = !canContinue;
		if (!canContinue) return;

		const defaultSpreadIndex = definition?.defaultSpreadIndex ?? 0;
		goTo(defaultSpreadIndex);
		setIsPreviewMode(true);
	}, [definition, goTo, setIsPreviewMode, validateDefinitionAsync]);

	const handleClick = () => {
		if (isAddToBasketDisabled) return;
		if (!isPreviewRequired || isPreviewMode) {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			onAddToBasketClick();
		} else {
			// eslint-disable-next-line @typescript-eslint/no-floating-promises
			onNextClick();
		}
	};

	if (areLabelsLoading) return null;
	return (
		<>
			<CtaButton
				className={className}
				onClick={handleClick}
				disabled={false}
				ref={ctaTextButtonRef}
				hidden={!showLargeButton}
			>
				{ctaButtonText}
			</CtaButton>
			<CtaButton
				className={className}
				onClick={handleClick}
				disabled={false}
				hidden={showLargeButton}
			>
				<Icon iconName={ctaButtonIconName} />
			</CtaButton>
		</>
	);
}

export default AddToCartButton;
